import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ExclamationIcon } from '@heroicons/react/solid';

/*
 * ConsentWithdrawnDialog (TDI-475)
 *
 * The blocking message a clinician or admin sees when the family withdraws
 * consent while they are working on the referral. It cannot be dismissed by
 * Escape or a click outside: the only ways on are the actions offered, so
 * nobody carries on typing into a record that has stopped under them.
 *
 * Built on the clinician workspace's own dialog pattern
 * (SendTriageLetterDialog): Radix Dialog, white card, plain copy. There is
 * no design frame for the BTH side; wording is a draft for Jasmine's gate.
 *
 * @param {boolean} open
 * @param {() => void} onLeave      "Go to dashboard"
 * @param {() => void} [onViewReadOnly]  Offered to the support side only
 *   (TDI-481): close the message and read the record without acting on it.
 */
export default function ConsentWithdrawnDialog({ open, onLeave, onViewReadOnly }) {
  return (
    <Dialog.Root open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content
          role="alertdialog"
          aria-describedby="consent-withdrawn-body"
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          className="fixed left-1/2 top-1/2 z-50 w-[calc(100%-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white p-6 shadow-xl focus:outline-none"
        >
          <div className="flex items-start gap-3">
            <ExclamationIcon className="mt-0.5 h-6 w-6 flex-shrink-0 text-amber-500" aria-hidden="true" />
            <div className="min-w-0">
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                Consent withdrawn
              </Dialog.Title>
              <Dialog.Description id="consent-withdrawn-body" className="mt-2 text-sm text-gray-700">
                The parent or carer has withdrawn their consent for this referral.
                Nothing further can happen on it, and it has left the triage lists.
              </Dialog.Description>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap justify-end gap-3">
            {typeof onViewReadOnly === 'function' && (
              <button
                type="button"
                onClick={onViewReadOnly}
                className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                View the record (read only)
              </button>
            )}
            <button
              type="button"
              autoFocus
              onClick={onLeave}
              className="inline-flex items-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
            >
              Go to dashboard
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
